import { Close, OpenInNew, Star, CheckCircle } from "@mui/icons-material";
import { Box, IconButton, Stack, ThemeProvider, Typography, Snackbar } from "@mui/material";
import Alert from "@mui/material/Alert";
import { SafeLeaseButton, SafeLeaseCard, SafeLeaseChip, theme } from "@safelease/components";
import { Image } from "../../image";
import { useAuth } from "../../auth";
import { mixpanelEventHandler } from "../../utilities/reactMixpanelHandler";
import { QueryResult, useQuery } from "@apollo/client";
import { Relationship } from "../../utilities/generated/gql-types";
import { getRelationship } from "../../queries";
import Cookies from "universal-cookie";
import { useEffect, useState } from "react";

export function PandCBanner({ source }: { source: string }) {
  const auth = useAuth();
  const cookies = new Cookies();
  const [hidden, setHidden] = useState<boolean>(cookies.get("pandc-banner-visibility") === "hidden");
  const [toastOpen, setToastOpen] = useState<boolean>(false);

  // For whatever reason the introduction of the cookie doesn't force rerender, so this change lister forces
  // the page to rerender after the cookie has been set
  useEffect(() => {
    cookies.addChangeListener(() => {
      if (cookies.get("pandc-banner-visibility") === "hidden") {
        setHidden(true);
      } else {
        setHidden(false);
      }
    });
  }, []);

  const userRelationshipId = auth.user?.relationshipId;

  // Fetch or use the cache'd relationship details to derive the CSM and AM IDs
  const relationshipQuery: QueryResult<{ getRelationship: Relationship }> = useQuery(getRelationship, {
    variables: {
      id: userRelationshipId,
    },
    skip: !userRelationshipId,
  });

  if (hidden) return null;

  return (
    <ThemeProvider theme={theme}>
      <SafeLeaseCard
        sx={{
          p: 2,
          color: "white",
          position: "relative",
          bgcolor: "rgb(17, 48, 99)",
          mb: 2,
          overflow: "hidden", // Prevent background image from bleeding out
          isolation: "isolate", // Create new stacking context for better Safari compatibility
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            opacity: 0.2,
            pointerEvents: "none", // Prevent interaction with background
          }}
        >
          <Image
            src="/images/wavy_backdrop.png"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>

        {/* Close button positioned absolutely in top right */}
        <IconButton
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "white",
            zIndex: 10,
            backgroundColor: "rgba(0, 0, 0, 0.2)", // Add background for better visibility
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.3)",
            },
          }}
          onClick={() => {
            cookies.set("pandc-banner-visibility", "hidden");
          }}
        >
          <Close />
        </IconButton>

        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          sx={{
            position: "relative",
            zIndex: 1,
            alignItems: "flex-start",
            WebkitBoxAlign: "flex-start", // Safari-specific flexbox property
          }}
        >
          {/* Column 1: Main Content */}
          <Stack
            direction="column"
            spacing={1}
            sx={{
              flex: 1,
              minWidth: 0,
              width: { xs: "100%", lg: "auto" }, // Explicit width for Safari
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Star sx={{ color: "yellow.main", flexShrink: 0 }} />
              <Typography variant="body1" sx={{ color: "white", WebkitTextFillColor: "white" }}>
                Facility Insurance
              </Typography>
              <SafeLeaseChip label="New" sx={{ bgcolor: "blue.main", color: "white" }} size="small" />
            </Stack>
            <Typography sx={{ color: "white", WebkitTextFillColor: "white", fontWeight: 700, fontSize: "18px" }}>
              Skip the broker and save 15-20%
            </Typography>
            <Typography
              sx={{
                color: "rgb(203,223,246)",
                WebkitTextFillColor: "rgb(203,223,246)",
                fontSize: "13px",
                pr: { xs: 0, md: 1 },
                lineHeight: 1.4,
              }}
            >
              SafeLease can now protect your facility, as well as your tenants. Purpose-built insurance for self-storage with claims handled
              in-house. Lower premiums, better coverage, more NOI.
            </Typography>
            <Box sx={{ mt: 1 }}>
              <SafeLeaseButton
                variant="outlined"
                size="small"
                sx={{ minWidth: 0, color: "white", borderColor: "white" }}
                onClick={() => {
                  let accessToken: string | undefined;
                  try {
                    const storedUser = localStorage.getItem("user");
                    accessToken = storedUser ? JSON.parse(storedUser)?.accessToken : undefined;
                  } catch {}
                  fetch("/api/salesforce/engagements", {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
                    },
                    body: JSON.stringify({ source }),
                  })
                    .then((res) => {
                      if (!res.ok) throw new Error(`HTTP ${res.status}`);
                      return res.json().catch(() => null);
                    })
                    .catch((err) => {
                      console.error("Engagement creation failed", err);
                      mixpanelEventHandler("P&C - Engagement Creation Failed", {
                        userId: auth.user.id,
                        userRelationshipId,
                        error: String(err?.message || err),
                      });
                    })
                    .then(() => {
                      setToastOpen(true);
                    });
                }}
              >
                Get a Quote
              </SafeLeaseButton>
              <SafeLeaseButton
                variant="text"
                size="small"
                sx={{ minWidth: 0, color: "white", borderColor: "white", ml: 2 }}
                endIcon={<OpenInNew sx={{ fontSize: "16px" }} />}
                onClick={() => {
                  mixpanelEventHandler("P&C - Learn More Button Clicked - SafeLease Home Page", {
                    csmId: relationshipQuery?.data?.getRelationship?.csm?.id,
                    amId: relationshipQuery?.data?.getRelationship?.am?.id,
                    userId: auth.user.id,
                    userRelationshipId,
                  });
                  window.open("https://www.safelease.com/commercial-insurance", "_blank");
                }}
              >
                Learn More
              </SafeLeaseButton>
            </Box>
          </Stack>

          {/* Column 2: What's Included */}
          <Stack
            direction="column"
            spacing={1}
            sx={{
              minWidth: 0,
              display: { xs: "none", md: "flex" },
              flex: "0 0 auto",
              width: { md: "250px", lg: "auto" }, // Fixed width for better Safari support
              maxWidth: "300px",
            }}
          >
            {/* Spacer to align with main headline */}
            <Box sx={{ height: "28px" }} />
            <Typography variant="body2" sx={{ color: "white", WebkitTextFillColor: "white", fontWeight: 600, mb: 0.5, fontSize: "16px" }}>
              What's Included
            </Typography>
            <Stack spacing={0.3}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "nowrap" }}>
                <CheckCircle sx={{ color: "yellow.main", fontSize: "14px", flexShrink: 0 }} />
                <Typography sx={{ color: "white", WebkitTextFillColor: "white", fontSize: "12px", lineHeight: 1.3 }}>
                  Property coverage up to $30M
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "nowrap" }}>
                <CheckCircle sx={{ color: "yellow.main", fontSize: "14px", flexShrink: 0 }} />
                <Typography sx={{ color: "white", WebkitTextFillColor: "white", fontSize: "12px", lineHeight: 1.3 }}>
                  $1M Sale & Disposal coverage
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "nowrap" }}>
                <CheckCircle sx={{ color: "yellow.main", fontSize: "14px", flexShrink: 0 }} />
                <Typography sx={{ color: "white", WebkitTextFillColor: "white", fontSize: "12px", lineHeight: 1.3 }}>
                  No coinsurance penalties
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "nowrap" }}>
                <CheckCircle sx={{ color: "yellow.main", fontSize: "14px", flexShrink: 0 }} />
                <Typography sx={{ color: "white", WebkitTextFillColor: "white", fontSize: "12px", lineHeight: 1.3 }}>
                  Single deductible across buildings
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {/* Column 3: Image Section - Hidden on mobile, visible on medium+ screens */}
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              minWidth: "200px",
              maxWidth: "240px",
              position: "relative",
              flexShrink: 0,
            }}
          >
            {/* Spacer to align with column headers */}
            <Box sx={{ height: "28px" }} />
            <Box
              sx={{
                width: "100%",
                height: "160px",
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: "rgba(0,0,0,0.1)", // Fallback background
              }}
            >
              <Image
                src="/images/happy-man-walking-out-of-warehouse.jpg"
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "8px",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>
          </Box>
        </Stack>
      </SafeLeaseCard>
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setToastOpen(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Thanks! Your account manager will be in touch shortly.
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
